import { ZERO_WEI } from '@kwenta/sdk/constants'
import { MIN_MARGIN_AMOUNT } from '@kwenta/sdk/constants'
import { isZero } from '@kwenta/sdk/utils'
import { truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import { ERROR_MESSAGES } from 'components/ErrorNotifier'
import Error from 'components/ErrorView'
import Connector from 'containers/Connector'
import useENS from 'hooks/useENS'
import { previewErrorI18n } from 'queries/futures/constants'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import {
	selectPerpsProvider,
	selectMarketIndexPrice,
	selectMarketPriceInfo,
	selectTradeOrderType,
	selectTradePanelOrderPrice,
	selectTradePanelInputs,
	selectLeverageSide,
} from 'state/futures/common/selectors'
import { setTradePanelDrawerOpen } from 'state/futures/reducer'
import {
	selectMaxLeverage,
	selectPendingDelayedOrder,
	selectMaxUsdSizeInput,
	selectTradePanelSLTPValidity,
} from 'state/futures/selectors'
import { createSmartMarginAccount } from 'state/futures/snxPerpsV2/actions'
import {
	selectIsCreatingAccount,
	selectIsMarketCapReached,
	selectLockedMarginInMarkets,
	selectPlaceOrderTranslationKey,
	selectSelectedSmartMarginPosition,
	selectSmartMarginAccount,
	selectSmartMarginLeverage,
	selectTotalAvailableMargin,
	selectSmartMarginTradePreview,
	selectTradePreviewError,
	selectTradePreviewStatus,
	selectV2MarketInfo,
} from 'state/futures/snxPerpsV2/selectors'
import { ManageModalType, PerpsProvider } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import { selectIsWatcherMode, selectWallet } from 'state/wallet/selectors'
import { orderPriceInvalidLabel } from 'utils/futures'

const SubmitSmartMarginTrade: React.FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { isWalletConnected } = Connector.useContainer()
	const { openConnectModal } = useConnectModal()

	const { susdSize } = useAppSelector(selectTradePanelInputs)
	const maxLeverageValue = useAppSelector(selectMaxLeverage)
	const selectedPerpsProvider = useAppSelector(selectPerpsProvider)
	const previewTrade = useAppSelector(selectSmartMarginTradePreview)
	const previewError = useAppSelector(selectTradePreviewError)
	const leverage = useAppSelector(selectSmartMarginLeverage)
	const orderType = useAppSelector(selectTradeOrderType)
	const openOrder = useAppSelector(selectPendingDelayedOrder)
	const leverageSide = useAppSelector(selectLeverageSide)
	const maxUsdInputAmount = useAppSelector(selectMaxUsdSizeInput)
	const isMarketCapReached = useAppSelector(selectIsMarketCapReached)
	const placeOrderTranslationKey = useAppSelector(selectPlaceOrderTranslationKey)
	const orderPrice = useAppSelector(selectTradePanelOrderPrice)
	const marketAssetRate = useAppSelector(selectMarketIndexPrice)
	const marketInfo = useAppSelector(selectV2MarketInfo)
	const indexPrice = useAppSelector(selectMarketPriceInfo)
	const previewStatus = useAppSelector(selectTradePreviewStatus)
	const smartMarginAccount = useAppSelector(selectSmartMarginAccount)
	const position = useAppSelector(selectSelectedSmartMarginPosition)
	const sltpValidity = useAppSelector(selectTradePanelSLTPValidity)
	const accountMargin = useAppSelector(selectTotalAvailableMargin)
	const lockedMargin = useAppSelector(selectLockedMarginInMarkets)
	const isCreatingAccount = useAppSelector(selectIsCreatingAccount)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const walletAddress = useAppSelector(selectWallet)
	const { ensName } = useENS((walletAddress || '') as string)

	const orderError = useMemo(() => {
		if (previewError) return t(previewErrorI18n(previewError))
		if (previewTrade?.statusMessage && previewTrade.statusMessage !== 'Success')
			return previewTrade?.statusMessage
		return null
	}, [previewTrade?.statusMessage, previewError, t])

	const increasingPosition = !position?.details.side || position?.details.side === leverageSide

	const onSubmit = useCallback(() => {
		dispatch(setTradePanelDrawerOpen(false))
		dispatch(setOpenModal('futures_confirm_smart_margin_trade'))
	}, [dispatch])

	const isDepositRequired = useMemo(() => {
		return accountMargin.lt(MIN_MARGIN_AMOUNT) && lockedMargin.eq(0)
	}, [accountMargin, lockedMargin])

	const otherReason = useMemo(() => {
		if (!isWalletConnected) {
			return { key: 'futures.market.trade.button.connect-wallet', action: openConnectModal }
		} else if (!smartMarginAccount) {
			return {
				key: 'futures.market.trade.button.create-account',
				action: () => dispatch(createSmartMarginAccount()),
			}
		} else if (isDepositRequired) {
			return {
				key: 'futures.market.trade.button.deposit-funds',
				action: () =>
					dispatch(
						setOpenModalWithParams({
							type: 'futures_deposit_withdraw_smart_margin',
							params: { tab: ManageModalType.Deposit },
						})
					),
			}
		}
		return undefined
	}, [isWalletConnected, smartMarginAccount, isDepositRequired, dispatch, openConnectModal])

	const placeOrderDisabledReason = useMemo<{
		message: string
		show?: 'warn' | 'error'
	} | null>(() => {
		if (orderError) {
			return { message: orderError, show: 'error' }
		}
		const maxLeverage = marketInfo?.appMaxLeverage ?? wei(1)

		// TODO: Clean up errors and warnings
		const indexPriceWei = indexPrice?.price ?? ZERO_WEI
		const canLiquidate =
			(previewTrade?.newSize.gt(0) && indexPriceWei.lt(previewTrade?.liqPrice)) ||
			(previewTrade?.newSize.lt(0) && indexPriceWei.gt(previewTrade?.liqPrice))
		if (canLiquidate) {
			return {
				show: 'warn',
				message: `Position can be liquidated`,
			}
		}

		if (leverage.gt(maxLeverageValue))
			return {
				show: 'warn',
				message: `Max leverage ${maxLeverage.toString(0)}x exceeded`,
			}
		if (marketInfo?.isSuspended)
			return {
				show: 'warn',
				message: `Market suspended`,
			}
		if (isMarketCapReached && increasingPosition)
			return {
				show: 'warn',
				message: `Open interest limit exceeded`,
			}

		const invalidReason = orderPriceInvalidLabel(
			orderPrice,
			leverageSide,
			marketAssetRate,
			orderType
		)

		if ((orderType === 'limit' || orderType === 'stop') && !!invalidReason)
			return {
				show: 'warn',
				message: invalidReason,
			}
		if (susdSize.gt(maxUsdInputAmount))
			return {
				show: 'warn',
				message: 'Max trade size exceeded',
			}
		if (
			placeOrderTranslationKey === 'futures.market.trade.button.deposit-margin-minimum' &&
			!isWatcherMode
		)
			return {
				show: 'warn',
				message: 'Min $50 margin required',
			}

		if (isZero(susdSize)) {
			return { message: 'Trade size required' }
		}
		if (orderType === 'market' && !!openOrder && !openOrder.isStale) {
			return {
				show: 'warn',
				message: ERROR_MESSAGES.ORDER_PENDING,
			}
		}
		if (selectedPerpsProvider === PerpsProvider.SNX_V2) {
			if (previewTrade?.status !== 0 || previewStatus.status === FetchStatus.Loading)
				return { message: 'awaiting_preview' }
			if (orderType !== 'market' && isZero(orderPrice)) return { message: 'trade price required' }
		}

		if (sltpValidity.stopLoss.invalidLabel) {
			return {
				show: 'warn',
				message: 'Invalid Stop Loss Price',
			}
		}

		return null
	}, [
		susdSize,
		orderType,
		openOrder,
		orderError,
		orderPrice,
		leverageSide,
		marketAssetRate,
		marketInfo?.isSuspended,
		placeOrderTranslationKey,
		maxUsdInputAmount,
		selectedPerpsProvider,
		isMarketCapReached,
		increasingPosition,
		previewStatus,
		maxLeverageValue,
		leverage,
		indexPrice,
		previewTrade,
		sltpValidity,
		marketInfo?.appMaxLeverage,
		isWatcherMode,
	])

	return (
		<>
			{isWatcherMode ? (
				<Error
					message={t('futures.market.trade.button.watching-address', {
						address: ensName || truncateAddress((walletAddress || '') as string),
					})}
					messageType="warn"
				/>
			) : (
				<div>
					<ManagePositionContainer>
						<PlaceOrderButton
							data-testid="trade-panel-submit-button"
							noOutline
							fullWidth
							loading={previewStatus.status === FetchStatus.Loading || isCreatingAccount}
							variant={otherReason ? 'yellow' : leverageSide}
							disabled={!otherReason && !!placeOrderDisabledReason}
							onClick={otherReason?.action ?? onSubmit}
							size="medium"
						>
							{t(otherReason?.key ?? placeOrderTranslationKey)}
						</PlaceOrderButton>
					</ManagePositionContainer>
				</div>
			)}

			{!otherReason && placeOrderDisabledReason?.show ? (
				<Error
					message={placeOrderDisabledReason.message}
					messageType={placeOrderDisabledReason.show}
				/>
			) : null}
		</>
	)
}

const ManagePositionContainer = styled.div`
	display: flex;
	grid-gap: 15px;
	margin-bottom: 16px;
`

const PlaceOrderButton = styled(TransactionButton)`
	height: 55px;
	text-align: center;
	white-space: normal;
`

export default SubmitSmartMarginTrade
